import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { usePaymentMutations } from '../hooks/useSalesDocs';
import type { SalesDoc, PaymentMethod } from '../types';

interface RecordPaymentModalProps {
  open: boolean;
  invoice: SalesDoc | null;
  onClose: () => void;
}

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'UPI', label: 'UPI / QR Code' },
  { value: 'Bank Transfer', label: 'Net Banking / IMPS' },
  { value: 'Cash', label: 'Cash Payment' },
  { value: 'Credit Card', label: 'Credit/Debit Card' },
  { value: 'Cheque', label: 'Cheque Payment' },
];

export function RecordPaymentModal({ open, invoice, onClose }: RecordPaymentModalProps) {
  const { logPayment } = usePaymentMutations();
  const [amount, setAmount] = useState<number | ''>('');
  const [method, setMethod] = useState<PaymentMethod>('UPI');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [refNum, setRefNum] = useState('');
  const [notes, setNotes] = useState('');

  const [prevOpen, setPrevOpen] = useState(open);
  const [prevInvoice, setPrevInvoice] = useState(invoice);
  if (open !== prevOpen || invoice !== prevInvoice) {
    setPrevOpen(open);
    setPrevInvoice(invoice);
    if (open && invoice) {
      setAmount(invoice.amount ?? '');
      setMethod('UPI');
      setDate(new Date().toISOString().split('T')[0]);
      setRefNum('');
      setNotes('');
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;
    if (!amount || Number(amount) <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }
    if (!date) {
      toast.error('Please select a payment date.');
      return;
    }

    logPayment.mutate(
      {
        invoice_id: invoice.id,
        amount_paid: Number(amount),
        payment_method: method,
        payment_date: date,
        transaction_reference: refNum.trim(),
        notes: notes.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Payment logged successfully.');
          onClose();
        },
        onError: (err: any) => {
          toast.error(getApiErrorMessage(err, 'log payments'));
        },
      }
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record Invoice Payment"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={logPayment.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="payment-form" loading={logPayment.isPending}>
            Save Payment
          </Button>
        </>
      }
    >
      <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded bg-surface-container-low p-3 border border-outline/30">
          <span className="text-body-sm text-on-surface-variant block">Invoice Title</span>
          <span className="font-semibold text-on-surface text-data-md block">{invoice?.title}</span>
          <div className="flex gap-4 mt-2">
            <div>
              <span className="text-[11px] text-on-surface-variant block uppercase font-semibold">Total Amount</span>
              <span className="font-bold text-primary text-data-lg">₹{invoice?.amount?.toLocaleString('en-IN') || 0}</span>
            </div>
            <div>
              <span className="text-[11px] text-on-surface-variant block uppercase font-semibold">Reference</span>
              <span className="text-on-surface text-data-md block font-medium">{invoice?.reference_number || '—'}</span>
            </div>
          </div>
        </div>

        {/* Amount Paid */}
        <Input
          label="Amount Paid (₹) *"
          name="amount_paid"
          type="number"
          min={1}
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value ? Number(e.target.value) : '')}
          placeholder="0"
        />

        {/* Date + Method Row */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Payment Date *"
            name="payment_date"
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Select
            label="Payment Method *"
            name="payment_method"
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            options={METHODS}
          />
        </div>

        {/* Transaction Ref */}
        <Input
          label="Transaction Ref / Chq No."
          name="ref_num"
          value={refNum}
          onChange={(e) => setRefNum(e.target.value)}
          placeholder="Txn ID, Cheque number…"
        />

        {/* Notes */}
        <div>
          <label htmlFor="payment-notes" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Private Notes
          </label>
          <textarea
            id="payment-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any internal remarks…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </form>
    </Modal>
  );
}
